import * as THREE from 'three';
import {ReplaySubject, type Observable, type Subscription} from 'rxjs';
import {
  isSketchArcObject,
  isSketchCollectionObject,
  isSketchLineObject,
  isSketchPointObject,
  SketchCollectionObject,
  SketchLineObject,
  SketchOverlayRenderStateApplier,
  SketchPointObject,
} from '../../mesh/sketch';
import type {CollectSource} from '../../mesh/sketch';
import {
  SketchChamferType,
  SketchCornerMode as SketchCornerModeEnum,
  SketchCornerSelectionItemKind,
  SketchCornerSelectionPhase,
  type SketchCornerControllerSnapshot,
  type SketchCornerSelectionItem,
} from './SketchCornerDrawingTypes';
import {EntityInteractionEventType, type EntitySelectedEventPayload} from '../../manager/EntitySelectionManager';
import type {Viewer} from '../../viewer/Viewer';
import {SketchChamferOperationService} from './SketchChamferOperationService';
import {SketchFilletOperationService} from './SketchFilletOperationService';
import {SketchCornerOperationContext} from './SketchCornerOperationContext';
import {
  SKETCH_CLOSE_TOLERANCE,
  type SketchCornerAnalysis,
  type SketchCornerLineData,
  type SketchCornerOperationOptions,
  type SketchCornerOperationResult,
} from './SketchCornerTypes';
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent';
import { convertMmToM } from '../../utils/unit';
import type {SketchGroupRuntimeHistorySnapshot} from '../../history/sketch';
import {SketchLineArcFilletGeometryResolver} from './SketchLineArcFilletGeometryResolver';
import {SketchCornerPointSelectionResolver} from './SketchCornerPointSelectionResolver';
import {createChamferAutoDimensions, createFilletAutoDimensions} from './createCornerAutoDimensions';
import type {EntityPatch} from '../../history/types';
import type {SketchRelationDefinition} from '../solver/constraint/relationDefinition/SketchRelationDefinition';
import {detachConstraintFromAllTargets} from '../solver/constraint/utils/detachConstraintFromAllTargets';

//------ 草图圆角预览状态 开始------
/**
 * 圆角预览颜色。
 */
const FILLET_PREVIEW_COLOR = '#ffd54a';

/**
 * 圆角预览线段名称。
 */
const FILLET_PREVIEW_NAME = '草图圆角预览圆弧';

/**
 * 创建草图圆角预览线材质。
 * @returns 草图圆角预览线材质。
 */
function createFilletPreviewLineMaterial(): THREE.LineBasicMaterial {
  const previewMaterial = new THREE.LineBasicMaterial({
    color: FILLET_PREVIEW_COLOR,
    linewidth: 3,
    transparent: true,
    opacity: 0.95,
    depthTest: false,
    depthWrite: false,
  });
  previewMaterial.name = '草图圆角预览-黄色前景材质';
  return previewMaterial;
}
//------ 草图圆角预览状态 结束------

//------ 草图圆角倒角全局历史 开始------
/**
 * 草图圆角/倒角模型历史命令类型。
 * 说明：圆角和倒角提交后都会改变草图实体、源线端点、约束和闭合轮廓，
 * 因此必须进入全局 Model Undo / Redo，而不是只保留命令内撤消。
 */
enum SketchCornerRuntimeHistoryCommandType {
  /** 草图圆角正式提交。 */
  Fillet = 'sketch.runtime.corner.fillet',
  /** 草图倒角正式提交。 */
  Chamfer = 'sketch.runtime.corner.chamfer',
}
//------ 草图圆角倒角全局历史 结束------

//------ 草图倒角命令内撤消 开始------
/**
 * 草图倒角命令内撤消记录。
 * 说明：
 * 1. SolidWorks 草图倒角 PropertyManager 的“撤消(U)”只回退当前命令内刚创建的倒角；
 * 2. 倒角会同时修改源线端点、控制点、约束和闭合集合顺序，因此必须使用整组草图运行时快照；
 * 3. 该记录不进入全局模型历史栈，避免命令内撤消污染 Ctrl+Z / Redo 分支。
 */
interface SketchChamferCommandUndoRecord {
  /** 倒角创建前的活动草图快照。 */
  beforeSnapshot: SketchGroupRuntimeHistorySnapshot;
}
//------ 草图倒角命令内撤消 结束------

/**
 * 草图圆角/倒角交互控制器。
 * 职责：
 * 1. 管理圆角源实体或倒角线段的顺序选择与提示；
 * 2. 管理圆角/倒角参数快照；
 * 3. 调度圆角/倒角执行服务并处理新实体归属。
 */
export class SketchCornerDrawingController {
  /** 实体选择事件订阅。 */
  private entitySelectedSubscription: Subscription | null = null;
  //------ 草图圆角实体面板快照 开始------
  /** 控制器快照事件流。 */
  private readonly snapshotSubject = new ReplaySubject<SketchCornerControllerSnapshot>(1);
  /** 当前命令是否激活。 */
  private active = false;
  //------ 草图圆角实体面板快照 结束------
  //------ 直线圆弧草图圆角选择状态 开始------
  /** 当前已锁定的第一条源实体选择。数组长度始终为 0 或 1。 */
  private selectedCornerSources: SketchCornerSourceSelection[] = [];
  //------ 直线圆弧草图圆角选择状态 结束------
  /** 当前圆角参数。 */
  private filletOptions: SketchFilletOptions = {
    radius: convertMmToM(10),
    keepConstrainedCorners: true,
    dimensionEachFillet: false,
  };
  /** 当前倒角参数。 */
  /** 本笔圆角/倒角事务待并入的自动尺寸补丁（HIST-001：与几何同一笔提交）。 */
  private pendingCornerDimensionPatches: EntityPatch[] = [];

  private chamferOptions: SketchChamferOptions = {
    chamferType: SketchChamferType.EqualDistance,
    distance1: convertMmToM(10),
    distance2: convertMmToM(10),
    angleDeg: 45,
  };
  /** 控制器运行对象。 */
  public readonly render: Viewer;
  /** 当前模式。 */
  public mode: SketchCornerModeEnum = SketchCornerModeEnum.Chamfer;
  /** 圆角/倒角共享上下文。 */
  private readonly operationContext: SketchCornerOperationContext;
  /** 圆角执行服务。 */
  private readonly filletOperationService: SketchFilletOperationService;
  //------ 直线圆弧草图圆角几何解析 开始------
  /** 直线—圆弧圆角几何解析器。 */
  private readonly lineArcFilletGeometryResolver: SketchLineArcFilletGeometryResolverLike;
  //------ 直线圆弧草图圆角几何解析 结束------
  /** 倒角执行服务。 */
  private readonly chamferOperationService: SketchChamferOperationService;
  /** 草图角点到源实体对的选择解析器。 */
  private readonly pointSelectionResolver: SketchCornerPointSelectionResolverLike;
  //------ 草图圆角预览状态 开始------
  /** 当前待确认圆角预览会话。 */
  private pendingCornerPreviewSession: PendingCornerPreviewSession | null = null;
  /** 下一次圆角预览在 PropertyManager 中展示的序号。 */
  private nextFilletPreviewDisplayIndex = 1;
  //------ 草图圆角预览状态 结束------
  //------ 草图倒角命令内撤消 开始------
  /** 当前草图倒角命令内可撤消记录栈。 */
  private readonly chamferCommandUndoRecords: SketchChamferCommandUndoRecord[] = [];
  //------ 草图倒角命令内撤消 结束------

  /**
   * 构造函数。
   * @param render 控制器运行对象。
   * @param lineArcFilletGeometryResolver 直线—圆弧圆角几何解析器。
   * @param pointSelectionResolver 草图角点选择解析器。
   */
  public constructor(
    render: Viewer,
    lineArcFilletGeometryResolver: SketchLineArcFilletGeometryResolverLike
      = new SketchLineArcFilletGeometryResolver(),
    pointSelectionResolver: SketchCornerPointSelectionResolverLike
      = new SketchCornerPointSelectionResolver(),
  ) {
    this.render = render;
    this.operationContext = new SketchCornerOperationContext(render);
    this.filletOperationService = new SketchFilletOperationService(this.operationContext);
    this.lineArcFilletGeometryResolver = lineArcFilletGeometryResolver;
    this.chamferOperationService = new SketchChamferOperationService(this.operationContext);
    this.pointSelectionResolver = pointSelectionResolver;
    this.notifyStateChanged();
  }

  /**
   * 启动指定模式并绑定选择监听。
   * @param mode 圆角/倒角模式。
   */
  public startMode(mode: SketchCornerModeEnum): void {
    this.mode = mode;
    this.active = true;
    this.clearPendingCornerPreviewSession();
    this.clearChamferCommandUndoRecords();
    this.selectedCornerSources = [];
    this.bindEntitySelectionEvent();
    this.notifyStateChanged();
  }

  /**
   * 清理模式运行态。
   * @param keepMode 是否保留当前模式值。
   */
  public clearMode(keepMode = false): void {
    this.active = false;
    this.entitySelectedSubscription?.unsubscribe();
    this.entitySelectedSubscription = null;
    this.clearPendingCornerPreviewSession();
    this.clearChamferCommandUndoRecords();
    this.selectedCornerSources = [];
    if (!keepMode) {
      this.mode = SketchCornerModeEnum.Chamfer;
    }
    this.notifyStateChanged();
  }

  //------ 草图圆角实体面板快照 开始------
  /**
   * 释放控制器资源。
   */
  public dispose(): void {
    this.clearMode(false);
    this.snapshotSubject.complete();
  }

  /**
   * 监听草图圆角/倒角控制器快照变化。
   * @returns 控制器快照事件流。
   */
  public observeSnapshot(): Observable<SketchCornerControllerSnapshot> {
    return this.snapshotSubject.asObservable();
  }

  /**
   * 获取当前控制器快照。
   * @returns 控制器快照。
   */
  public getSnapshot(): SketchCornerControllerSnapshot {
    const hasPendingFilletPreview = this.hasPendingFilletPreview();
    return {
      active: this.active,
      mode: this.mode,
      phase: this.resolveSelectionPhase(hasPendingFilletPreview),
      selectedItems: this.buildSelectionItems(),
      firstSelection: this.getFirstSelectedEntitySnapshot(),
      hasPendingFilletPreview,
      canConfirm: hasPendingFilletPreview,
    };
  }

  /**
   * 解析当前选择阶段。
   * @param hasPendingFilletPreview 当前是否存在待确认圆角预览。
   * @returns 草图圆角/倒角选择阶段。
   */
  private resolveSelectionPhase(hasPendingFilletPreview: boolean): SketchCornerSelectionPhase {
    if (!this.active) {
      return SketchCornerSelectionPhase.Idle;
    }
    if (hasPendingFilletPreview) {
      return SketchCornerSelectionPhase.PendingFilletPreview;
    }
    if (this.selectedCornerSources.length > 0) {
      return SketchCornerSelectionPhase.SelectSecondEntity;
    }
    return SketchCornerSelectionPhase.SelectFirstEntity;
  }

  /**
   * 构建 PropertyManager 中展示的实体列表。
   * @returns 草图圆角/倒角选择列表项。
   */
  private buildSelectionItems(): SketchCornerSelectionItem[] {
    const pendingFilletPreviewSession = this.pendingCornerPreviewSession;
    if (pendingFilletPreviewSession?.mode === SketchCornerModeEnum.Fillet) {
      return [{
        id: `sketch-fillet-preview:${pendingFilletPreviewSession.displayIndex}`,
        label: `圆角<${pendingFilletPreviewSession.displayIndex}>`,
        kind: SketchCornerSelectionItemKind.FilletPreview,
        objectId: null,
        segmentIndex: null,
      }];
    }

    const firstSelection = this.selectedCornerSources[0];
    if (!firstSelection) {
      return [];
    }
    const firstSelectedEntity = firstSelection.object;

    return [{
      id: firstSelectedEntity.uuid,
      label: this.resolveSourceDisplayLabel(firstSelectedEntity),
      kind: isSketchArcObject(firstSelectedEntity)
        ? SketchCornerSelectionItemKind.Arc
        : SketchCornerSelectionItemKind.Line,
      objectId: firstSelectedEntity.uuid,
      segmentIndex: null,
    }];
  }

  /**
   * 解析源实体展示名称。
   * @param source 源直线或圆弧。
   * @returns PropertyManager 中展示的实体名称。
   */
  private resolveSourceDisplayLabel(source: SketchFilletSourceObject): string {
    const sourceName = source.name.trim();
    if (sourceName) {
      return sourceName;
    }
    // 兜底只在旧数据缺失 name 时使用，避免面板出现空行。
    const entityName = isSketchArcObject(source) ? '圆弧' : '直线';
    return `${entityName} (${source.uuid.slice(0, 8)})`;
  }

  /**
   * 派发控制器状态变化。
   */
  private notifyStateChanged(): void {
    this.snapshotSubject.next(this.getSnapshot());
  }
  //------ 草图圆角实体面板快照 结束------

  /**
   * 获取当前收集到的线对象快照。
   * @returns 线对象数组副本。
   */
  public getSelectedLinesSnapshot(): SketchLineObject[] {
    return this.selectedCornerSources
      .map((selection) => selection.object)
      .filter((object): object is SketchLineObject => isSketchLineObject(object));
  }

  /**
   * 获取当前第一条已锁定实体的引用快照。
   * @returns 第一条实体引用；未锁定时返回 null。
   */
  public getFirstSelectedEntitySnapshot(): SketchCornerEntityRef | null {
    const firstSelection = this.selectedCornerSources[0];
    if (!firstSelection) {
      return null;
    }
    return {
      objectId: firstSelection.object.uuid,
      segmentIndex: null,
    };
  }

  /**
   * 获取当前第一条已锁定实体的引用快照。
   *
   * @deprecated 请使用 `getFirstSelectedEntitySnapshot`。
   * @returns 第一条实体引用；未锁定时返回 null。
   */
  public getFirstSelectedLineSnapshot(): SketchCornerEntityRef | null {
    return this.getFirstSelectedEntitySnapshot();
  }

  /**
   * 获取圆角参数快照。
   * @returns 圆角参数副本。
   */
  public getFilletOptionsSnapshot(): SketchFilletOptions {
    return {
      ...this.filletOptions,
    };
  }

  /**
   * 获取倒角参数快照。
   * @returns 倒角参数副本。
   */
  public getChamferOptionsSnapshot(): SketchChamferOptions {
    return {
      ...this.chamferOptions,
    };
  }

  /**
   * 重置当前顺序选择状态。
   */
  public resetSelection(): void {
    this.selectedCornerSources = [];
    this.notifyStateChanged();
  }

  /**
   * 更新圆角参数。
   * @param options 圆角参数增量。
   */
  public setFilletOptions(options: Partial<SketchFilletOptions>): void {
    this.filletOptions = {
      ...this.filletOptions,
      ...options,
    };
    this.refreshPendingFilletPreview();
  }

  /**
   * 更新倒角参数。
   * @param options 倒角参数增量。
   */
  public setChamferOptions(options: Partial<SketchChamferOptions>): void {
    this.chamferOptions = {
      ...this.chamferOptions,
      ...options,
    };
  }

  //------ 草图倒角命令内撤消 开始------
  /**
   * 判断当前草图倒角命令内是否存在可撤消记录。
   * @returns 存在可撤消倒角记录时返回 true。
   */
  public hasChamferCommandUndo(): boolean {
    return this.chamferCommandUndoRecords.length > 0;
  }

  /**
   * 撤消当前草图倒角命令内最近一次倒角。
   * @returns 撤消成功返回 true；没有可撤消记录时返回 false。
   */
  public undoLastChamferCommandStep(): boolean {
    const undoRecord = this.chamferCommandUndoRecords.pop() ?? null;
    if (!undoRecord) {
      return false;
    }

    /**
     * 整组快照恢复会替换草图子树并重建端点、约束和闭合集合，
     * 这样比只删除倒角线更接近 SolidWorks 的“撤消刚才倒角”语义。
     */
    this.render.restoreSketchGroupRuntimeHistorySnapshot(undoRecord.beforeSnapshot);
    this.render.releaseSketchGroupRuntimeHistorySnapshot(undoRecord.beforeSnapshot);
    this.resetSelection();
    this.render.drawingNoticeCallback?.('已撤消上一次草图倒角。', 'info');
    return true;
  }
  //------ 草图倒角命令内撤消 结束------

  /**
   * 判断当前是否存在待确认圆角预览。
   * @returns 存在待确认圆角预览返回 true。
   */
  public hasPendingFilletPreview(): boolean {
    return this.pendingCornerPreviewSession?.mode === SketchCornerModeEnum.Fillet
      && this.pendingCornerPreviewSession.previewObject !== null;
  }

  /**
   * 确认当前待提交圆角预览，写入正式实体。
   * @returns 提交成功返回 true；无待确认预览或提交失败返回 false。
   */
  public confirmPendingFilletPreview(): boolean {
    const session = this.pendingCornerPreviewSession;
    if (!session || session.mode !== SketchCornerModeEnum.Fillet || !session.previewObject) {
      return false;
    }

    //------ 草图圆角倒角全局历史 开始------
    /**
     * 圆角预览本身不进入历史；只有用户点击确认后创建的正式圆角才提交模型历史。
     * 快照由 Viewer 捕获活动草图整组状态，覆盖源线端点、圆弧、控制点、约束和轮廓顺序。
     */
    try {
      return this.render.executeActiveSketchRuntimeHistoryTransaction({
        label: '草图圆角',
        commandType: SketchCornerRuntimeHistoryCommandType.Fillet,
        work: () => this.commitPendingFilletPreviewSession(session),
        shouldCommit: (confirmed) => confirmed,
        resolveExtraPatches: () => this.takePendingCornerDimensionPatches(),
      });
    } catch (error) {
      this.rollbackPendingCornerDimensionPatches();
      this.render.drawingNoticeCallback?.(
        `草图圆角参数求解失败：${error instanceof Error ? error.message : String(error)}`,
        'warning',
      );
      return false;
    }
    //------ 草图圆角倒角全局历史 结束------
  }

  //------ 草图圆角倒角全局历史 开始------
  /**
   * 执行当前圆角预览会话的正式提交。
   * @param session 待提交圆角预览会话。
   * @returns 提交成功返回 true。
   */
  private commitPendingFilletPreviewSession(session: PendingCornerPreviewSession): boolean {
    if (this.pendingCornerPreviewSession !== session || session.mode !== SketchCornerModeEnum.Fillet) {
      return false;
    }

    //------ 圆角保留约束开关-延迟关系删除 开始------
    /**
     * 只捕获提交前已经存在的关系 ID。
     * 圆角服务成功后再按该快照删除，既保证预览不改变模型，也不会误删新建的相切与半径关系。
     */
    const relationIdsToRemove = this.filletOptions.keepConstrainedCorners
      ? new Set<number>()
      : this.captureGeometricRelationIdsForSelectedEntities(
        session.firstSelection.object,
        session.secondSelection.object,
      );
    //------ 圆角保留约束开关-延迟关系删除 结束------

    const result = this.applySketchCornerOperation(
      SketchCornerModeEnum.Fillet,
      session.firstSelection,
      session.secondSelection,
      this.buildOperationOptionsByMode(),
    );
    if (!result.success || !result.createdObject || !result.controlPoints) {
      this.render.drawingNoticeCallback?.(result.message, 'warning');
      return false;
    }

    this.addCreatedEntityToSketch(
      result.createdObject,
      result.controlPoints,
      session.targetContainer,
      session.collectionInsertOrderContext,
    );
    //------ 圆角自动半径尺寸 开始------
    /** SW 草图圆角默认带半径尺寸；半径锁由该尺寸的关系视图提供（服务不再挂匿名半径关系）。 */
    if (isSketchArcObject(result.createdObject)) {
      this.appendCornerAutoDimensionPatches((repository, sketchId) => createFilletAutoDimensions({
        repository,
        sketchId,
        arc: result.createdObject as Parameters<typeof createFilletAutoDimensions>[0]['arc'],
      }));
    }
    //------ 圆角自动半径尺寸 结束------
    /**
     * 新圆角、两条相切关系、自动半径尺寸与活动草图既有关系只进入一次参数求解。
     * 求解失败先撤销尺寸仓库候选，再由外层运行时历史事务恢复草图 before。
     */
    if (!this.render.solveActiveSketchParametricCornerOperation()) {
      this.rollbackPendingCornerDimensionPatches();
      throw new Error('完整活动草图参数岛拒绝圆角候选');
    }
    //------ 圆角保留约束开关-延迟关系删除 开始------
    this.removeGeometricRelationsByIds(relationIdsToRemove);
    //------ 圆角保留约束开关-延迟关系删除 结束------
    this.clearPendingCornerPreviewSession();
    this.render.requestRender();
    this.render.drawingNoticeCallback?.(result.message, 'info');
    this.resetSelection();
    return true;
  }
  //------ 草图圆角倒角全局历史 结束------

  /**
   * 取消当前待提交圆角预览。
   */
  public cancelPendingFilletPreview(): void {
    if (this.pendingCornerPreviewSession?.mode !== SketchCornerModeEnum.Fillet) {
      return;
    }
    this.clearPendingCornerPreviewSession();
    this.resetSelection();
    this.render.requestRender();
  }

  /**
   * 绑定实体选择事件。
   */
  private bindEntitySelectionEvent(): void {
    if (this.entitySelectedSubscription) {
      return;
    }
    const manager = this.render.entitySelectionManager;
    if (!manager) {
      return;
    }
    this.entitySelectedSubscription = manager
      .observe(EntityInteractionEventType.EntitySelected)
      .subscribe((payload) => this.handleEntitySelected(payload));
  }

  /**
   * 处理实体选择事件。
   * @param payload 选择事件载荷。
   */
  private handleEntitySelected(payload: EntitySelectedEventPayload): void {
    if (this.pendingCornerPreviewSession) {
      this.render.drawingNoticeCallback?.('当前倒角或圆角存在待确认预览，请先确认或取消', 'info');
      return;
    }

    //------ 草图圆角倒角角点选择 开始------
    /**
     * SolidWorks 允许直接选择一个角点/顶点完成一组源实体输入。
     * 点选择必须先在 cad-core 中还原为实体对，再进入既有几何、预览和历史链路，
     * 避免把 SketchPointObject 直接扩散到圆角/倒角执行服务。
     */
    if (payload.object && isSketchPointObject(payload.object)) {
      this.handleCornerPointSelected(payload.object);
      return;
    }
    //------ 草图圆角倒角角点选择 结束------

    //------ 直线圆弧草图圆角选择门禁 开始------
    /**
     * 圆角允许直线或圆弧，倒角继续只允许直线。
     * 类型门禁必须停在命令控制器边界，不能让无效实体进入 PropertyManager 状态。
     */
    const isSupportedFilletSource = payload.object
      && (isSketchLineObject(payload.object) || isSketchArcObject(payload.object));
    if (
      payload.object
      && (
        (this.mode === SketchCornerModeEnum.Fillet && !isSupportedFilletSource)
        || (this.mode === SketchCornerModeEnum.Chamfer && !isSketchLineObject(payload.object))
      )
    ) {
      const message = this.mode === SketchCornerModeEnum.Fillet
        ? '草图圆角仅支持选择直线或圆弧。'
        : '草图倒角仅支持选择草图直线。';
      this.render.drawingNoticeCallback?.(message, 'info');
      return;
    }
    //------ 直线圆弧草图圆角选择门禁 结束------

    const currentSelection = this.resolvePrimaryCornerSelection(payload);
    if (!currentSelection) {
      return;
    }

    const firstSelection = this.selectedCornerSources[0];
    if (!firstSelection) {
      this.selectedCornerSources = [currentSelection];
      this.notifyStateChanged();
      this.render.drawingNoticeCallback?.('已选择第一个草图实体，请继续选择第二个草图实体', 'info');
      return;
    }

    if (firstSelection.object.uuid === currentSelection.object.uuid) {
      return;
    }

    if (
      this.mode === SketchCornerModeEnum.Fillet
      && isSketchArcObject(firstSelection.object)
      && isSketchArcObject(currentSelection.object)
    ) {
      this.render.drawingNoticeCallback?.('当前草图圆角暂不支持圆弧—圆弧，请选择一条直线。', 'warning');
      return;
    }

    this.applyCornerOperationFromSelection(firstSelection, currentSelection);
  }

  //------ 草图圆角倒角角点选择 开始------
  /**
   * 处理草图角点选择。
   *
   * @param point 用户选择的草图角点。
   */
  private handleCornerPointSelected(point: SketchPointObject): void {
    const sourceSelections = this.pointSelectionResolver.resolve(point, this.mode);
    if (!sourceSelections) {
      const message = this.mode === SketchCornerModeEnum.Fillet
        ? '所选草图顶点必须仅连接两条直线，或一条直线与一条圆弧。'
        : '所选草图顶点必须仅连接两条直线。';
      this.render.drawingNoticeCallback?.(message, 'warning');
      return;
    }

    /**
     * 有效角点本身已经表达完整实体对，因此覆盖可能残留的单实体顺序选择，
     * 并直接复用统一操作入口，保证点选与双实体选择生成完全相同的结果。
     */
    this.selectedCornerSources = [];
    this.applyCornerOperationFromSelection(sourceSelections[0], sourceSelections[1]);
  }
  //------ 草图圆角倒角角点选择 结束------

  /**
   * 解析主选圆角/倒角实体及鼠标命中位置。
   *
   * 设计说明：
   * 1. 普通 EntitySelected 事件只负责稳定对象语义；
   * 2. 分支选择需要的精确命中位置从选择管理器现有 Intersection 快照读取；
   * 3. 命令状态只保存普通坐标元组，不持有或响应式装饰 Three.js Intersection。
   *
   * @param payload 选择事件载荷。
   * @returns 主选实体选择；无有效实体时返回 null。
   */
  private resolvePrimaryCornerSelection(payload: EntitySelectedEventPayload): SketchCornerSourceSelection | null {
    if (
      !payload.object
      || (!isSketchLineObject(payload.object) && !isSketchArcObject(payload.object))
    ) {
      return null;
    }
    const selectedPick = this.render.entitySelectionManager
      ?.getSelectedPicks()
      .find((pick) => pick.object.uuid === payload.object?.uuid);
    const hitPoint = selectedPick?.point;
    return {
      object: payload.object,
      hitPoint: hitPoint && [hitPoint.x, hitPoint.y, hitPoint.z].every((value) => Number.isFinite(value))
        ? [hitPoint.x, hitPoint.y, hitPoint.z]
        : null,
    };
  }

  //------ 两直线圆角命中选支修复 开始------
  /**
   * 根据两条直线的选择命中位置解析角点分支。
   *
   * 该方法是交互选择与底层几何上下文之间的唯一适配入口，确保预览、正式提交和
   * 闭合集合角点解析使用完全相同的命中坐标，避免三条链路产生不同圆角象限。
   *
   * @param firstLineData 第一条直线几何数据。
   * @param secondLineData 第二条直线几何数据。
   * @param firstSelection 第一条直线选择。
   * @param secondSelection 第二条直线选择。
   * @returns 两直线角点解析结果；不存在有效交点时返回 null。
   */
  private resolveLineLineCornerAnalysis(
    firstLineData: SketchCornerLineData,
    secondLineData: SketchCornerLineData,
    firstSelection: SketchCornerSourceSelection,
    secondSelection: SketchCornerSourceSelection,
  ): SketchCornerAnalysis | null {
    const firstHitPoint = firstSelection.hitPoint;
    const secondHitPoint = secondSelection.hitPoint;

    // 选择状态仅保存普通坐标元组，此处临时构造 Vector3 交给几何层，不进入 Vue 响应式状态。
    const firstHitWorld = firstHitPoint
      ? new THREE.Vector3(firstHitPoint[0], firstHitPoint[1], firstHitPoint[2])
      : null;
    const secondHitWorld = secondHitPoint
      ? new THREE.Vector3(secondHitPoint[0], secondHitPoint[1], secondHitPoint[2])
      : null;
    return this.operationContext.resolveCornerAnalysis(
      firstLineData,
      secondLineData,
      firstHitWorld,
      secondHitWorld,
    );
  }
  //------ 两直线圆角命中选支修复 结束------

  /**
   * 根据当前模式执行圆角/倒角。
   * @param firstSelection 第一个源实体选择。
   * @param secondSelection 第二个源实体选择。
   */
  private applyCornerOperationFromSelection(
    firstSelection: SketchCornerSourceSelection,
    secondSelection: SketchCornerSourceSelection,
  ): void {
    const firstSource = firstSelection.object;
    const secondSource = secondSelection.object;
    const collectionSelectionValidationMessage = this.validateSketchCollectionSelection(
      firstSource,
      secondSource,
    );
    if (collectionSelectionValidationMessage) {
      this.render.drawingNoticeCallback?.(collectionSelectionValidationMessage, 'warning');
      this.resetSelection();
      return;
    }

    const targetContainer = this.resolveCreatedEntityContainer(firstSource, secondSource);
    const selectedCornerPoints = targetContainer && isSketchCollectionObject(targetContainer)
      ? this.resolveSelectedCornerPoints(firstSelection, secondSelection)
      : null;
    const collectionInsertOrderContext = this.resolveCollectionInsertOrderContext(
      firstSource,
      secondSource,
      selectedCornerPoints,
    );
    if (targetContainer && isSketchCollectionObject(targetContainer) && !collectionInsertOrderContext) {
      this.render.drawingNoticeCallback?.('当前选择无法确定在封闭集合中的插入顺序，请选择同一轮廓内相邻实体', 'warning');
      this.resetSelection();
      return;
    }

    if (this.mode === SketchCornerModeEnum.Fillet) {
      const previewPrepared = this.preparePendingFilletPreview(
        firstSelection,
        secondSelection,
        targetContainer,
        collectionInsertOrderContext,
      );
      if (!previewPrepared) {
        return;
      }
      this.render.drawingNoticeCallback?.('已生成圆角预览，请点击右上角勾确认，或点击叉取消', 'info');
      return;
    }

    if (!isSketchLineObject(firstSource) || !isSketchLineObject(secondSource)) {
      this.render.drawingNoticeCallback?.('草图倒角仅支持两条直线。', 'warning');
      return;
    }
    this.applyChamferOperationWithCommandUndo(
      firstSelection,
      secondSelection,
      targetContainer,
      collectionInsertOrderContext,
    );
  }

  //------ 草图倒角命令内撤消 开始------
  /**
   * 执行倒角并记录命令内撤消快照。
   * @param firstSelection 第一条线及其命中位置。
   * @param secondSelection 第二条线及其命中位置。
   * @param targetContainer 目标容器。
   * @param collectionInsertOrderContext 集合插入顺序上下文。
   */
  private applyChamferOperationWithCommandUndo(
    firstSelection: SketchCornerSourceSelection,
    secondSelection: SketchCornerSourceSelection,
    targetContainer: THREE.Object3D | null,
    collectionInsertOrderContext: SketchCollectionInsertOrderContext | null,
  ): void {
    //------ 草图圆角倒角全局历史 开始------
    /**
     * 倒角选择第二条线后会即时创建正式实体，因此这里就是模型历史事务边界。
     * 命令内撤消快照在 work 内部单独捕获，两套历史互不替代。
     */
    this.render.executeActiveSketchRuntimeHistoryTransaction({
      label: '草图倒角',
      commandType: SketchCornerRuntimeHistoryCommandType.Chamfer,
      work: () => this.applyChamferOperationWithCommandUndoInHistoryTransaction(
        firstSelection,
        secondSelection,
        targetContainer,
        collectionInsertOrderContext,
      ),
      shouldCommit: (applied) => applied,
      resolveExtraPatches: () => this.takePendingCornerDimensionPatches(),
    });
    //------ 草图圆角倒角全局历史 结束------
  }

  /**
   * 在全局历史事务内部执行倒角并记录命令内撤消快照。
   * @param firstSelection 第一条线及其命中位置。
   * @param secondSelection 第二条线及其命中位置。
   * @param targetContainer 目标容器。
   * @param collectionInsertOrderContext 集合插入顺序上下文。
   * @returns 倒角创建成功返回 true。
   */
  private applyChamferOperationWithCommandUndoInHistoryTransaction(
    firstSelection: SketchCornerSourceSelection,
    secondSelection: SketchCornerSourceSelection,
    targetContainer: THREE.Object3D | null,
    collectionInsertOrderContext: SketchCollectionInsertOrderContext | null,
  ): boolean {
    const beforeSnapshot = this.render.captureActiveSketchRuntimeHistorySnapshot('草图倒角命令内撤消') ?? null;
    const releaseBeforeSnapshot = (): void => {
      if (beforeSnapshot) {
        this.render.releaseSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
      }
    };

    try {
      const result = this.applySketchCornerOperation(
        this.mode,
        firstSelection,
        secondSelection,
        this.buildOperationOptionsByMode(),
      );
      if (!result.success) {
        releaseBeforeSnapshot();
        this.render.drawingNoticeCallback?.(result.message, 'warning');
        return false;
      }
      if (!result.createdObject || !result.controlPoints) {
        throw new Error('草图倒角创建成功但缺少实体或控制点');
      }

      this.addCreatedEntityToSketch(
        result.createdObject,
        result.controlPoints,
        targetContainer,
        collectionInsertOrderContext,
      );
      //------ 倒角自动距离尺寸 开始------
      /**
       * SW 倒角默认带两条距离尺寸（交点焦点 → 各倒角端点）；
       * 距离锁由尺寸的关系视图提供（服务不再挂匿名距离关系）。
       * 角度-距离倒角仍走角度关系，本轮不建角度尺寸。
       */
      if (this.chamferOptions.chamferType !== SketchChamferType.AngleDistance) {
        const chamferLine = result.createdObject;
        const focusPoint = result.controlPoints[result.controlPoints.length - 1];

        if (isSketchLineObject(chamferLine) && focusPoint && isSketchPointObject(focusPoint)) {
          this.appendCornerAutoDimensionPatches((repository, sketchId) => createChamferAutoDimensions({
            repository,
            sketchId,
            focusPoint,
            firstPoint: chamferLine.startPoint,
            secondPoint: chamferLine.endPoint,
          }));
        }
      }
      //------ 倒角自动距离尺寸 结束------
      /** 倒角实体、交点/角度关系、自动距离尺寸与既有约束只执行一次完整参数求解。 */
      if (!this.render.solveActiveSketchParametricCornerOperation()) {
        this.rollbackPendingCornerDimensionPatches();
        if (!beforeSnapshot) {
          throw new Error('完整活动草图参数岛拒绝倒角候选，且缺少可恢复快照');
        }
        this.render.restoreSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
        releaseBeforeSnapshot();
        this.render.drawingNoticeCallback?.('完整活动草图参数岛拒绝倒角候选', 'warning');
        return false;
      }
      this.render.requestRender();
      this.render.drawingNoticeCallback?.(result.message, 'info');
      if (beforeSnapshot) {
        /**
         * 撤消记录必须先入栈再刷新选择快照。
         * PropertyManager 的“撤消(U)”按钮会在快照回调里同步读取 `hasChamferCommandUndo()`，
         * 若先 resetSelection 再入栈，UI 会读到旧的空栈并保持禁用。
         */
        this.chamferCommandUndoRecords.push({beforeSnapshot});
      }
      this.resetSelection();
      return true;
    } catch (error) {
      /**
       * 全局历史事务会负责恢复 before；这里仅释放命令内撤消快照，
       * 避免同一次异常路径重复恢复草图组。
       */
      releaseBeforeSnapshot();
      this.rollbackPendingCornerDimensionPatches();
      throw error;
    }
  }
  //------ 草图倒角命令内撤消 结束------

  //------ 圆角倒角自动尺寸补丁通道 开始------
  /**
   * 在当前圆角/倒角事务内创建自动尺寸并暂存补丁。
   *
   * 仓库或活动草图缺失时静默跳过（SDK 无尺寸环境下圆角/倒角照常可用）。
   *
   * @param create 尺寸创建回调（收仓库与草图持久 ID）。
   */
  private appendCornerAutoDimensionPatches(
    create: (repository: NonNullable<ReturnType<Viewer['getSketchDimensionRepository']>>, sketchId: string) => EntityPatch[],
  ): void {
    const repository = this.render.getSketchDimensionRepository?.() ?? null;
    const sketchId = this.render.sketchGroupManager?.activeSketchGroup?.persistentId ?? null;

    if (!repository || !sketchId) {
      return;
    }
    this.pendingCornerDimensionPatches.push(...create(repository, sketchId));
  }

  /**
   * 取走并清空暂存的自动尺寸补丁（事务 resolveExtraPatches 消费）。
   *
   * @returns 补丁列表。
   */
  private takePendingCornerDimensionPatches(): EntityPatch[] {
    const patches = this.pendingCornerDimensionPatches;

    this.pendingCornerDimensionPatches = [];
    return patches;
  }

  /**
   * 撤销当前圆角/倒角尚未进入历史事务的自动尺寸候选。
   *
   * 自动尺寸补丁当前全部是 create；统一参数求解拒绝候选时按补丁实体 ID 通过
   * 尺寸仓库历史恢复通道幂等删除，避免几何回滚后留下孤立 driving 尺寸。
   */
  private rollbackPendingCornerDimensionPatches(): void {
    const repository = this.render.getSketchDimensionRepository?.() ?? null;
    if (repository) {
      this.pendingCornerDimensionPatches.forEach((patch) => {
        repository.removeDimensionForRestore(patch.entityId);
      });
    }
    this.pendingCornerDimensionPatches = [];
  }
  //------ 圆角倒角自动尺寸补丁通道 结束------

  /**
   * 创建待确认圆角预览。
   * @param firstSelection 第一个源实体选择。
   * @param secondSelection 第二个源实体选择。
   * @param targetContainer 目标容器。
   * @param collectionInsertOrderContext 集合插入顺序上下文。
   * @returns 预览是否创建成功。
   */
  private preparePendingFilletPreview(
    firstSelection: SketchCornerSourceSelection,
    secondSelection: SketchCornerSourceSelection,
    targetContainer: THREE.Object3D | null,
    collectionInsertOrderContext: SketchCollectionInsertOrderContext | null,
  ): boolean {
    const previewObject = this.createFilletPreviewObject(firstSelection, secondSelection);
    if (!previewObject) {
      this.render.drawingNoticeCallback?.('当前参数无法生成圆角预览', 'warning');
      this.resetSelection();
      return false;
    }

    this.clearPendingCornerPreviewSession();
    addObject3DToParent(previewObject, this.render.scene);
    this.pendingCornerPreviewSession = {
      mode: SketchCornerModeEnum.Fillet,
      displayIndex: this.nextFilletPreviewDisplayIndex,
      firstSelection,
      secondSelection,
      targetContainer,
      collectionInsertOrderContext,
      previewObject,
    };
    this.nextFilletPreviewDisplayIndex += 1;
    this.render.requestRender();
    this.notifyStateChanged();
    return true;
  }

  /**
   * 刷新当前待确认圆角预览。
   */
  private refreshPendingFilletPreview(): void {
    const session = this.pendingCornerPreviewSession;
    if (!session || session.mode !== SketchCornerModeEnum.Fillet) {
      return;
    }
    const nextPreviewObject = this.createFilletPreviewObject(
      session.firstSelection,
      session.secondSelection,
    );
    if (!nextPreviewObject) {
      /**
       * 当前参数已经失效时必须立即移除旧预览。
       * 会话和两次源选择继续保留，用户把参数改回可行域后可直接重建预览。
       */
      if (session.previewObject?.parent) {
        session.previewObject.parent.remove(session.previewObject);
      }
      if (session.previewObject) {
        this.disposePreviewObject(session.previewObject);
      }
      session.previewObject = null;
      this.render.drawingNoticeCallback?.('当前参数暂时无法更新圆角预览，请继续调整参数', 'warning');
      this.render.requestRender();
      this.notifyStateChanged();
      return;
    }

    if (session.previewObject?.parent) {
      session.previewObject.parent.remove(session.previewObject);
    }
    if (session.previewObject) {
      this.disposePreviewObject(session.previewObject);
    }
    addObject3DToParent(nextPreviewObject, this.render.scene);
    session.previewObject = nextPreviewObject;
    this.render.requestRender();
  }

  /**
   * 清理当前待确认圆角预览会话。
   */
  private clearPendingCornerPreviewSession(): void {
    const session = this.pendingCornerPreviewSession;
    if (!session) {
      return;
    }
    if (session.previewObject?.parent) {
      session.previewObject.parent.remove(session.previewObject);
    }
    if (session.previewObject) {
      this.disposePreviewObject(session.previewObject);
    }
    this.pendingCornerPreviewSession = null;
  }

  /**
   * 释放圆角预览对象占用的渲染资源。
   * @param previewObject 预览对象。
   */
  private disposePreviewObject(previewObject: THREE.Line): void {
    previewObject.geometry.dispose();
    if (!previewObject.material) {
      return;
    }
    if (Array.isArray(previewObject.material)) {
      previewObject.material.forEach((material) => material.dispose());
      return;
    }
    previewObject.material.dispose();
  }

  /**
   * 基于两个源实体与当前圆角参数创建预览圆弧对象。
   * @param firstSelection 第一个源实体选择。
   * @param secondSelection 第二个源实体选择。
   * @returns 预览圆弧对象；无法生成时返回 null。
   */
  private createFilletPreviewObject(
    firstSelection: SketchCornerSourceSelection,
    secondSelection: SketchCornerSourceSelection,
  ): THREE.Line | null {
    const radius = Number(this.filletOptions.radius ?? 10);
    if (!Number.isFinite(radius) || radius <= 0) {
      return null;
    }

    let arcPoints: THREE.Vector3[] | null = null;
    if (
      isSketchLineObject(firstSelection.object)
      && isSketchLineObject(secondSelection.object)
    ) {
      const firstLineData = this.operationContext.resolveCornerLineData(firstSelection.object);
      const secondLineData = this.operationContext.resolveCornerLineData(secondSelection.object);
      if (!firstLineData || !secondLineData) {
        return null;
      }
      //------ 两直线圆角命中选支修复 开始------
      const cornerAnalysis = this.resolveLineLineCornerAnalysis(
        firstLineData,
        secondLineData,
        firstSelection,
        secondSelection,
      );
      //------ 两直线圆角命中选支修复 结束------
      if (!cornerAnalysis) {
        return null;
      }
      const angle = this.operationContext.resolveCornerAngle(cornerAnalysis);
      if (!angle) {
        return null;
      }
      arcPoints = this.operationContext.resolveFilletGeometry(
        cornerAnalysis,
        angle,
        radius,
      )?.arcPoints ?? null;
    } else {
      arcPoints = this.resolveLineArcFilletGeometry(
        firstSelection,
        secondSelection,
        radius,
      )?.arcPoints ?? null;
    }
    if (!arcPoints || arcPoints.length < 2) {
      return null;
    }

    const previewGeometry = new THREE.BufferGeometry().setFromPoints(arcPoints);
    /**
     * 预览线属于待确认命令反馈，关闭深度测试和深度写入，
     * 避免共面草图、实体面或已有边线把圆角预览遮住。
     */
    const previewMaterial = createFilletPreviewLineMaterial();
    const previewObject = new THREE.Line(previewGeometry, previewMaterial);
    previewObject.name = FILLET_PREVIEW_NAME;
    previewObject.renderOrder = SketchOverlayRenderStateApplier.COMMAND_PREVIEW_RENDER_ORDER;
    previewObject.frustumCulled = false;
    return previewObject;
  }

  //------ 直线圆弧草图圆角几何解析 开始------
  /**
   * 标准化选择顺序并解析直线—圆弧圆角几何。
   * @param firstSelection 第一个源实体选择。
   * @param secondSelection 第二个源实体选择。
   * @param radius 圆角半径。
   * @returns 几何结果；选择组合非法或无有效候选时返回 null。
   */
  private resolveLineArcFilletGeometry(
    firstSelection: SketchCornerSourceSelection,
    secondSelection: SketchCornerSourceSelection,
    radius: number,
  ): SketchLineArcFilletGeometryResult | null {
    if (
      isSketchLineObject(firstSelection.object)
      && isSketchArcObject(secondSelection.object)
    ) {
      return this.lineArcFilletGeometryResolver.resolve({
        lineSelection: {
          object: firstSelection.object,
          hitPoint: firstSelection.hitPoint,
        },
        arcSelection: {
          object: secondSelection.object,
          hitPoint: secondSelection.hitPoint,
        },
        radius,
      });
    }
    if (
      isSketchArcObject(firstSelection.object)
      && isSketchLineObject(secondSelection.object)
    ) {
      return this.lineArcFilletGeometryResolver.resolve({
        lineSelection: {
          object: secondSelection.object,
          hitPoint: secondSelection.hitPoint,
        },
        arcSelection: {
          object: firstSelection.object,
          hitPoint: firstSelection.hitPoint,
        },
        radius,
      });
    }
    return null;
  }
  //------ 直线圆弧草图圆角几何解析 结束------

  //------ 草图倒角命令内撤消 开始------
  /**
   * 释放当前倒角命令内撤消栈。
   */
  private clearChamferCommandUndoRecords(): void {
    this.chamferCommandUndoRecords.splice(0).forEach((record) => {
      this.render.releaseSketchGroupRuntimeHistorySnapshot(record.beforeSnapshot);
    });
  }
  //------ 草图倒角命令内撤消 结束------


  /**
   * 校验圆角/倒角的两个源实体是否满足 SketchCollectionObject 选择规则。
   * @param firstSource 第一个源实体。
   * @param secondSource 第二个源实体。
   * @returns 通过返回 null；不通过返回提示文案。
   */
  private validateSketchCollectionSelection(
    firstSource: SketchFilletSourceObject,
    secondSource: SketchFilletSourceObject,
  ): string | null {
    const firstSketchCollection = firstSource.closedCollection && isSketchCollectionObject(firstSource.closedCollection)
      ? firstSource.closedCollection
      : null;
    const secondSketchCollection = secondSource.closedCollection && isSketchCollectionObject(secondSource.closedCollection)
      ? secondSource.closedCollection
      : null;

    if (!firstSketchCollection && !secondSketchCollection) {
      return null;
    }
    if (
      firstSketchCollection
      && secondSketchCollection
      && firstSketchCollection === secondSketchCollection
    ) {
      return null;
    }
    return '仅支持选择两个都不属于封闭集合的实体，或同一封闭集合内的相邻实体';
  }

  //------ 圆角保留约束开关-延迟关系删除 开始------
  /**
   * 捕获两个源实体当前关联的全部几何关系 ID。
   * @param firstSource 第一个源实体。
   * @param secondSource 第二个源实体。
   * @returns 提交前关系 ID 快照。
   */
  private captureGeometricRelationIdsForSelectedEntities(
    firstSource: SketchFilletSourceObject,
    secondSource: SketchFilletSourceObject,
  ): Set<number> {
    const constraintIdSet = new Set<number>();
    firstSource.constraints.getRelationSnapshots().forEach((snapshot) => {
      constraintIdSet.add(snapshot.id);
    });
    secondSource.constraints.getRelationSnapshots().forEach((snapshot) => {
      constraintIdSet.add(snapshot.id);
    });
    return constraintIdSet;
  }

  /**
   * 在当前草图对象树中按提交前快照删除几何关系。
   *
   * @param constraintIdSet 待删除关系 ID。
   */
  private removeGeometricRelationsByIds(constraintIdSet: ReadonlySet<number>): void {
    if (constraintIdSet.size <= 0) {
      return;
    }

    const interactionStateManager = this.render.interactionStateManager;
    const sketchObjects = [
      ...this.render.getSketchEdgeObjects(),
      ...this.render.getSketchPointObjects(),
    ];
    const relationMap = new Map<number, SketchRelationDefinition>();

    // 先解析逻辑关系实例，再从它的全部目标统一解绑，避免外部镜像轴留下半断开关系。
    sketchObjects.forEach((object) => {
      constraintIdSet.forEach((constraintId) => {
        const relation = object.constraints.getRelation(constraintId);
        if (relation) {
          relationMap.set(constraintId, relation);
        }
      });
    });

    relationMap.forEach((relation) => {
      sketchObjects.forEach((object) => {
        if (object.constraints.getRelation(relation.id) === relation) {
          interactionStateManager?.removeObjectConstraintMarker(object, relation.id);
        }
      });
      detachConstraintFromAllTargets(relation);
    });

    const sketchGroup = this.render.sketchGroupManager?.activeSketchGroup ?? null;
    sketchGroup?.getSketchReferenceTargets().forEach((referenceTarget) => {
      if (referenceTarget.constraints.getRelations().length > 0) {
        return;
      }
      sketchGroup.removeSketchReferenceTarget(referenceTarget);
      referenceTarget.dispose();
    });
  }
  //------ 圆角保留约束开关-延迟关系删除 结束------

  /**
   * 根据当前模式构造操作参数。
   * @returns 操作参数。
   */
  private buildOperationOptionsByMode(): SketchCornerOperationOptions {
    if (this.mode === SketchCornerModeEnum.Fillet) {
      return {
        radius: this.filletOptions.radius,
        dimensionEachFillet: this.filletOptions.dimensionEachFillet,
      };
    }
    return {
      chamferType: this.chamferOptions.chamferType,
      distance1: this.chamferOptions.distance1,
      distance2: this.chamferOptions.distance2,
      angleDeg: this.chamferOptions.angleDeg,
    };
  }

  /**
   * 执行圆角/倒角操作。
   * @param mode 当前模式。
   * @param firstSelection 第一个源实体选择。
   * @param secondSelection 第二个源实体选择。
   * @param options 操作参数。
   * @returns 执行结果。
   */
  private applySketchCornerOperation(
    mode: SketchCornerModeEnum,
    firstSelection: SketchCornerSourceSelection,
    secondSelection: SketchCornerSourceSelection,
    options: SketchCornerOperationOptions,
  ): SketchCornerOperationResult {
    const firstEntity = firstSelection.object;
    const secondEntity = secondSelection.object;

    //------ 直线圆弧草图圆角执行 开始------
    if (
      mode === SketchCornerModeEnum.Fillet
      && (
        (isSketchLineObject(firstEntity) && isSketchArcObject(secondEntity))
        || (isSketchArcObject(firstEntity) && isSketchLineObject(secondEntity))
      )
    ) {
      const radius = Number(options.radius ?? 10);
      const geometry = this.resolveLineArcFilletGeometry(
        firstSelection,
        secondSelection,
        radius,
      );
      if (!geometry) {
        return {
          success: false,
          message: '当前直线、圆弧与半径组合无法生成有效圆角',
        };
      }
      const isConstruction = Boolean(firstEntity.isConstruction && secondEntity.isConstruction);
      return this.filletOperationService.executeLineArc(geometry, isConstruction);
    }
    //------ 直线圆弧草图圆角执行 结束------

    if (!isSketchLineObject(firstEntity) || !isSketchLineObject(secondEntity)) {
      return {
        success: false,
        message: mode === SketchCornerModeEnum.Fillet
          ? '草图圆角仅支持直线—直线或直线—圆弧'
          : '草图倒角仅支持两条直线',
      };
    }

    const firstLineData = this.operationContext.resolveCornerLineData(firstEntity);
    const secondLineData = this.operationContext.resolveCornerLineData(secondEntity);
    if (!firstLineData || !secondLineData) {
      return {success: false, message: '线段几何数据无效'};
    }

    //------ 两直线圆角命中选支修复 开始------
    const cornerAnalysis = this.resolveLineLineCornerAnalysis(
      firstLineData,
      secondLineData,
      firstSelection,
      secondSelection,
    );
    //------ 两直线圆角命中选支修复 结束------
    if (!cornerAnalysis) {
      return {
        success: false,
        message: mode === SketchCornerModeEnum.Fillet
          ? '圆角仅支持存在交点的两条线段'
          : '倒角仅支持存在交点的两条线段',
      };
    }

    const angle = this.operationContext.resolveCornerAngle(cornerAnalysis);
    if (!angle) {
      return {success: false, message: '两线段夹角无效，无法创建圆角/倒角'};
    }

    const isConstruction = Boolean(firstLineData.isConstruction && secondLineData.isConstruction);
    if (mode === SketchCornerModeEnum.Fillet) {
      return this.filletOperationService.execute(cornerAnalysis, angle, options, isConstruction);
    }
    return this.chamferOperationService.execute(cornerAnalysis, angle, options, isConstruction);
  }

  /**
   * 解析新建实体应挂载到的目标容器。
   * @param firstSource 第一个源实体。
   * @param secondSource 第二个源实体。
   * @returns 目标容器；返回 null 表示加入当前草图。
   */
  private resolveCreatedEntityContainer(
    firstSource: SketchFilletSourceObject,
    secondSource: SketchFilletSourceObject,
  ): THREE.Object3D | null {
    const firstClosedCollection = firstSource.closedCollection;
    const secondClosedCollection = secondSource.closedCollection;
    if (!firstClosedCollection && !secondClosedCollection) {
      return null;
    }
    if (
      firstClosedCollection
      && secondClosedCollection
      && firstClosedCollection !== secondClosedCollection
    ) {
      throw new Error('两个源实体分别属于不同的封闭几何，无法确定新建圆角实体归属');
    }
    return firstClosedCollection ?? secondClosedCollection;
  }

  //------ 直线圆弧草图圆角集合角点解析 开始------
  /**
   * 解析本次操作实际使用的两个源角点控制点。
   *
   * @param firstSelection 第一个源实体选择。
   * @param secondSelection 第二个源实体选择。
   * @returns 两个源角点；几何无效时返回 null。
   */
  private resolveSelectedCornerPoints(
    firstSelection: SketchCornerSourceSelection,
    secondSelection: SketchCornerSourceSelection,
  ): SketchPointObject[] | null {
    if (
      isSketchLineObject(firstSelection.object)
      && isSketchLineObject(secondSelection.object)
    ) {
      const firstLineData = this.operationContext.resolveCornerLineData(firstSelection.object);
      const secondLineData = this.operationContext.resolveCornerLineData(secondSelection.object);
      if (!firstLineData || !secondLineData) {
        return null;
      }
      //------ 两直线圆角命中选支修复 开始------
      const analysis = this.resolveLineLineCornerAnalysis(
        firstLineData,
        secondLineData,
        firstSelection,
        secondSelection,
      );
      //------ 两直线圆角命中选支修复 结束------
      return analysis
        ? [analysis.firstUsage.cornerPoint, analysis.secondUsage.cornerPoint]
        : null;
    }

    const geometry = this.resolveLineArcFilletGeometry(
      firstSelection,
      secondSelection,
      Number(this.filletOptions.radius ?? 10),
    );
    return geometry
      ? [geometry.lineOldCornerPoint, geometry.arcOldCornerPoint]
      : null;
  }
  //------ 直线圆弧草图圆角集合角点解析 结束------

  /**
   * 解析封闭集合中新建边对象应插入的位置上下文。
   *
   * 设计说明：
   * 集合自身的 `collectSet` 已保存轮廓遍历顺序，因此直接按相邻索引确定插入位置，
   * 不再把顺序推断绑死在“两条直线的起终点角色”上，天然支持直线—圆弧混合轮廓。
   *
   * @param firstSource 第一个源实体。
   * @param secondSource 第二个源实体。
   * @returns 插入顺序上下文；无需写入集合顺序时返回 null。
   */
  private resolveCollectionInsertOrderContext(
    firstSource: SketchFilletSourceObject,
    secondSource: SketchFilletSourceObject,
    selectedCornerPoints: readonly SketchPointObject[] | null = null,
  ): SketchCollectionInsertOrderContext | null {
    const firstCollection = firstSource.closedCollection;
    const secondCollection = secondSource.closedCollection;
    if (!firstCollection || !secondCollection) {
      return null;
    }
    if (!isSketchCollectionObject(firstCollection) || !isSketchCollectionObject(secondCollection)) {
      return null;
    }
    if (firstCollection !== secondCollection) {
      return null;
    }

    const currentSources = Array.from(firstCollection.collectSet);
    if (currentSources.length < 2) {
      return null;
    }
    const firstIndex = currentSources.findIndex((source) => source.uuid === firstSource.uuid);
    const secondIndex = currentSources.findIndex((source) => source.uuid === secondSource.uuid);
    if (firstIndex < 0 || secondIndex < 0) {
      return null;
    }

    /**
     * 两条边构成的封闭轮廓在顺序上同时互为前后邻居，必须结合有向遍历端点判断
     * 用户选择的是哪一个角；测试桩或旧数据无法提供端点时，稳定回退到 collectSet 顺序。
     */
    if (currentSources.length === 2) {
      if (selectedCornerPoints && selectedCornerPoints.length > 0) {
        const traversal = SketchCollectionObject.resolveClosedGeometryTraversal(currentSources);
        if (traversal) {
          for (let stepIndex = 0; stepIndex < traversal.length; stepIndex += 1) {
            const currentStep = traversal[stepIndex];
            const nextStep = traversal[(stepIndex + 1) % traversal.length];
            if (!currentStep || !nextStep) {
              continue;
            }
            const currentEndpoints = SketchCollectionObject.getTraversalStepEndpoints(currentStep);
            const nextEndpoints = SketchCollectionObject.getTraversalStepEndpoints(nextStep);
            const matchesSelectedCorner = selectedCornerPoints.every((point) => (
              point === currentEndpoints.endPoint
              || point === nextEndpoints.startPoint
              || (
                point.getPosition().distanceTo(currentEndpoints.endPoint.getPosition())
                  <= SKETCH_CLOSE_TOLERANCE
                && point.getPosition().distanceTo(nextEndpoints.startPoint.getPosition())
                  <= SKETCH_CLOSE_TOLERANCE
              )
            ));
            if (!matchesSelectedCorner) {
              continue;
            }
            return {
              targetCollection: firstCollection,
              previousSource: currentStep.source,
              nextSource: nextStep.source,
            };
          }
        }
      }
      const previousSource = currentSources[0];
      const nextSource = currentSources[1];
      if (!previousSource || !nextSource) {
        return null;
      }
      return {
        targetCollection: firstCollection,
        previousSource,
        nextSource,
      };
    }

    if ((firstIndex + 1) % currentSources.length === secondIndex) {
      return {
        targetCollection: firstCollection,
        previousSource: firstSource,
        nextSource: secondSource,
      };
    }
    if ((secondIndex + 1) % currentSources.length === firstIndex) {
      return {
        targetCollection: firstCollection,
        previousSource: secondSource,
        nextSource: firstSource,
      };
    }
    return null;
  }

  /**
   * 将创建实体及相关控制点加入草图。
   * @param entity 实体对象。
   * @param controlPoints 控制点集合。
   * @param targetContainer 目标容器；为空时加入当前草图。
   * @param collectionInsertOrderContext 封闭集合插入顺序上下文。
   */
  private addCreatedEntityToSketch(
    entity: THREE.Line,
    controlPoints: SketchPointObject[],
    targetContainer: THREE.Object3D | null,
    collectionInsertOrderContext: SketchCollectionInsertOrderContext | null,
  ): void {
    this.render.interactionStateManager?.syncExternalMaterialSnapshot(entity);
    controlPoints.forEach((point) => {
      this.render.interactionStateManager?.syncExternalMaterialSnapshot(point);
    });

    if (targetContainer) {
      this.render.addObject(entity, targetContainer);
    } else {
      this.render.addSketchObject(entity);
    }
    controlPoints.forEach((point) => {
      if (point.parent === entity) {
        return;
      }
      if (targetContainer) {
        if (point.parent !== targetContainer) {
          this.render.addObject(point, targetContainer);
        }
        return;
      }
      this.render.addSketchObject(point);
    });
    this.syncClosedCollectionCollectSet(targetContainer, entity, collectionInsertOrderContext);
    this.render.notifySketchEntityCreated(entity);
  }

  /**
   * 将新建实体加入父封闭集合的 `collectSet`。
   * @param targetContainer 目标容器。
   * @param createdEntity 新建实体。
   * @param collectionInsertOrderContext 集合插入顺序上下文。
   */
  private syncClosedCollectionCollectSet(
    targetContainer: THREE.Object3D | null,
    createdEntity: THREE.Line,
    collectionInsertOrderContext: SketchCollectionInsertOrderContext | null,
  ): void {
    if (!targetContainer) {
      return;
    }
    if (!isSketchCollectionObject(targetContainer)) {
      return;
    }
    if (!isSketchLineObject(createdEntity) && !isSketchArcObject(createdEntity)) {
      throw new Error('圆角创建出的实体类型非法，无法写入封闭集合。');
    }
    if (!collectionInsertOrderContext) {
      throw new Error('缺少封闭集合插入顺序上下文，无法写入集合。');
    }
    if (collectionInsertOrderContext.targetCollection !== targetContainer) {
      throw new Error('封闭集合插入上下文目标不匹配，无法写入集合。');
    }

    const currentSources = Array.from(targetContainer.collectSet);
    if (currentSources.length < 2) {
      throw new Error('封闭集合源数量不足，无法插入新实体。');
    }
    const previousSourceIndex = currentSources.findIndex(
      (source) => source.uuid === collectionInsertOrderContext.previousSource.uuid,
    );
    const nextSourceIndex = currentSources.findIndex(
      (source) => source.uuid === collectionInsertOrderContext.nextSource.uuid,
    );
    if (previousSourceIndex < 0 || nextSourceIndex < 0) {
      throw new Error('未找到相邻源实体，无法插入新实体。');
    }
    if ((previousSourceIndex + 1) % currentSources.length !== nextSourceIndex) {
      throw new Error('封闭集合中源实体顺序异常，无法插入新实体。');
    }

    createdEntity.attachClosedCollection(targetContainer);

    // `Set` 不支持按索引插入，这里基于当前有序数组重建集合顺序。
    const nextOrderedSources: CollectSource[] = [];
    for (let sourceIndex = 0; sourceIndex < currentSources.length; sourceIndex += 1) {
      const currentSource = currentSources[sourceIndex];
      if (!currentSource) {
        throw new Error('封闭集合源数据缺失，无法重建顺序。');
      }
      nextOrderedSources.push(currentSource);
      if (sourceIndex === previousSourceIndex) {
        nextOrderedSources.push(createdEntity);
      }
    }
    targetContainer.collectSet.clear();
    nextOrderedSources.forEach((source) => {
      targetContainer.collectSet.add(source);
    });

    targetContainer.refreshContourGeometry();
  }
}
